
import Vue from "vue"
import { mapActions, mapGetters } from "vuex"
import { ValidationObserver } from "vee-validate"
import axios from "axios"
import { patternCheck } from "~/utils/block"
import { formatNumberWithLocale } from "~/utils/formatNumberWithLocale"
import { GET_LISTING, GET_ITEMS_COUNT } from "~/store/listing/getters"
import {
  ListingService,
  getV4Url,
  getV4UrlByCategoryId,
  getV4ListingPayload,
  addPreListingIdToSessionStorage,
  isValidLocation
} from "~/services/public/Listing"
import {
  AVAILABILITY_REMARK,
  CALL_US,
  ITEM_IDENTIFIER,
  RETRIEVE_QUOTE,
  VALIDATION
} from "~/constants/blocks"
import { redirectToV2 } from "~/utils/v4-redirect"
import { GET_PHONE_NUMBER_VALUE } from "~/store/phone-number/getters"
import eventBus from "~/scripts/bus"
import { SET_DELIVERY_PROPERTY_TYPE, SET_PICKUP_PROPERTY_TYPE } from "~/store/listing/actions"
import { SET_INVENTORY, SET_WINDOW_WIDTH } from "~/store/pages/actions"
import { extractHref, getInnerText } from "~/scripts/useful-functions"
import { getFurnitureDefaultInventory } from "~/services/public/ItemService"
import {
  GET_INVENTORY_DESKTOP,
  GET_IS_SMALL_OR_MEDIUM,
  GET_WINDOW_WIDTH
} from "~/store/pages/getters"
import { CATEGORY_V4_ID_FURNITURE } from "~/constants/category"

interface ComponentData {
  submittedForm: boolean
  submittingForm: boolean
  emailFormShow: boolean
  trustpilotCustomers: string
  showFurnitureItems: boolean | null
  initInventory: boolean
  itemCategories: any
  hasExtensionState: boolean
}

export default Vue.extend({
  name: "LocationForm",
  components: { ValidationObserver },

  props: {
    categoryV4Id: { type: Number, default: 0 },
    isCtaForm: { type: Boolean },
    hasExtension: { type: Boolean },
    block: { type: Object as () => Block, required: true }
  },

  data(): ComponentData {
    return {
      submittedForm: false,
      submittingForm: false,
      emailFormShow: false,
      trustpilotCustomers: "0",
      showFurnitureItems: null,
      initInventory: false,
      itemCategories: null,
      hasExtensionState: this.hasExtension
    }
  },

  computed: {
    ...mapGetters("pages", {
      getWindowWidth: GET_WINDOW_WIDTH,
      isSmallOrMedium: GET_IS_SMALL_OR_MEDIUM,
      getInventoryDesktop: GET_INVENTORY_DESKTOP
    }),
    ...mapGetters("listing", {
      getListing: GET_LISTING,
      getItemsCount: GET_ITEMS_COUNT
    }),
    ...mapGetters("phone-number", {
      getPhoneNumberValue: GET_PHONE_NUMBER_VALUE
    }),

    isLargeOrAbove(): any {
      return !this.isSmallOrMedium
    },

    locationBlock(): any {
      return this.categoryV4Id ? this.block.innerBlocks[0] : this.block.innerBlocks[1]
    },

    ctaBlock(): any {
      return this.block.innerBlocks[this.block.innerBlocks.length - 1]
    },

    ctaFormButtons(): any[] {
      if (this.isCtaForm) {
        return this.block.innerBlocks[0].innerBlocks[0].innerBlocks[0].innerBlocks
      }
      return []
    },

    ctaFormHasMultipleButtons(): boolean {
      if (this.isCtaForm) {
        return this.ctaFormButtons.length > 1
      }
      return false
    },

    getInventoryComputed(): boolean {
      return (this.isSmallOrMedium || this.hasExtensionState) && !this.hasItemIdentifier
    },

    hasAvailabilityRemark(): boolean {
      return patternCheck(this.block.attributes, AVAILABILITY_REMARK)
    },

    hasItemIdentifier(): boolean {
      return patternCheck(this.block.attributes, ITEM_IDENTIFIER)
    },

    hasCallUs(): boolean {
      return patternCheck(this.block.attributes, CALL_US)
    },

    hasRetrieveQuote(): boolean {
      return patternCheck(this.block.attributes, RETRIEVE_QUOTE)
    },

    isValidForm(): boolean {
      const listing = this.getListing
      if (this.hasValidation(this.locationBlock)) {
        if (!isValidLocation(listing?.pickup) || !isValidLocation(listing?.delivery)) {
          return false
        }
      }

      return true
    },

    hasItemsDesktop(): boolean {
      return this.isLargeOrAbove && this.hasExtensionState && this.getItemsCount
    },

    formattedTrustpilotCustomers(): string {
      return formatNumberWithLocale(this.trustpilotCustomers, this.$i18n.locale)
    }
  },

  watch: {
    onSubmittingFormChanges(val) {
      if (val) {
        eventBus.$emit("submitting-location-form")
      } else {
        eventBus.$emit("submitted-location-form")
      }
    }
  },

  async created() {
    this.$store.dispatch("pages/" + SET_INVENTORY, null)
    const trustPilotConfig = this.$config.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotAPIKey = trustPilotConfig.apiKey

    if (this.$i18n.locale !== "en") {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }
    const trustpilotURL = `https://api.trustpilot.com/v1/business-units/${trustPilotId}`
    try {
      const axiosInstance = axios.create() as any
      const response = await axiosInstance.get(trustpilotURL, {
        params: {
          apikey: trustPilotAPIKey
        }
      })
      if (response.status === 200 && response.data) {
        this.trustpilotCustomers = response.data.numberOfReviews.total
      }
    } catch (err) {
      console.log("Trustpilot error", err)
    }
  },

  async mounted() {
    if (!this.initInventory) {
      this.initInventory = true
      // Safety mechanism to disable IoLP Desktop when LocationFormExtension is missing
      if (this.hasExtensionState && !this.getInventoryDesktop) {
        this.hasExtensionState = false
      }
      if (this.getInventoryComputed) {
        this.itemCategories = (await getFurnitureDefaultInventory()).items
      } else {
        // @ts-ignore-next-line
        window.dataLayer?.push({ event: "show_responseiq" })
      }

      const inventoryMobile = this.getInventoryComputed && this.isSmallOrMedium
      await this.$store.dispatch("pages/" + SET_INVENTORY, inventoryMobile)

      // const inventoryDesktop = this.getInventoryComputed && !this.getIsSmallOrMedium
      // if (inventoryDesktop) {
      //   // Enable Sentry Replay tagged with Snowplow Domain UserID
      //   const { getReplay, setTag } = await import("@sentry/vue")
      //   getReplay()?.start()
      //   setTag("sp.domain_userid", this.$snowplow.getSnowplowDomainIds().domain_userid)
      // }

      const ua = navigator.userAgent.toLowerCase()
      // The implemented workaround only works on Safari & Chrome with iOS 16 & iOS 17
      if (
        ua.includes("safari") &&
        navigator.maxTouchPoints > 2 &&
        (ua.includes("version/16") ||
          ua.includes("version/17") ||
          ua.includes("iphone os 16_") ||
          ua.includes("iphone os 17_"))
      ) {
        if (ua.includes("chrome")) {
          document.body.classList.add("chrome")
        } else {
          document.body.classList.add("safari")
        }
      }
    }
  },

  beforeMount() {
    eventBus.$on("submit-location-form", this.submitForm)
    if (!this.initInventory) {
      window.addEventListener("resize", () => {
        this.setMobileOrDesktop()
      })
    }
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.setMobileOrDesktop)
    eventBus.$off("submit-location-form")
  },

  methods: {
    ...mapActions("listing", {
      setPickupPropertyType: SET_PICKUP_PROPERTY_TYPE,
      setDeliveryPropertyType: SET_DELIVERY_PROPERTY_TYPE
    }),

    extractHref,
    getInnerText,

    setMobileOrDesktop() {
      this.$store.dispatch("pages/" + SET_WINDOW_WIDTH, window.innerWidth)
    },

    async tryToOpenFurnitureItemsModal() {
      if (!this.itemCategories) {
        this.itemCategories = (await getFurnitureDefaultInventory()).items
      }
      if (this.isValidForm) {
        this.showFurnitureItems = true
      } else {
        this.$snowplow.trackIOLPButtonDisabled(this.getItemsCount, "Form")
        await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate()
      }
    },

    scrollInputToTop(el) {
      if (this.getWindowWidth && this.getWindowWidth > 768) return
      // @ts-ignore
      let element
      switch (el) {
        case "pickup":
          element = this.$refs.pickupInput
          break
        case "delivery":
          element = this.$refs.deliveryInput
          break
        default:
          return
      }
      if (element && element.$el.offsetTop) {
        window.scrollTo({
          top: element.$el.offsetTop - 25,
          behavior: "smooth"
        })
      }
    },

    async submitForm(isCalledFromPage = true) {
      if (this.submittingForm) return
      this.submittedForm = true
      this.submittingForm = true
      try {
        const listing = this.getListing

        const isFirstStepValid = await (
          this.$refs.observer as InstanceType<typeof ValidationObserver>
        ).validate()

        let isValid = isFirstStepValid

        const isSecondStepValid =
          this.$i18n.locale === "es" || !this.getInventoryComputed || !!this.getItemsCount
        if (isValid) {
          isValid = isSecondStepValid
        }

        if (!isValid && this.$i18n.locale === "en") {
          if (this.getInventoryComputed && isCalledFromPage) {
            const errors = (isFirstStepValid ? "" : "Form") + (isSecondStepValid ? "" : "Items")
            this.$snowplow.trackCTAButtonDisabled("page", this.getItemsCount, errors)
          }
          this.submittingForm = false
          eventBus.$emit("close-dropdown")
          this.hideItems()
          return
        }

        if (!isValid && this.$i18n.locale === "es") {
          this.submittingForm = false
          return
        }

        if (this.getInventoryComputed && isCalledFromPage) {
          this.$snowplow.trackCTAButton("page", this.getItemsCount)
        }

        let categoryV4Id = CATEGORY_V4_ID_FURNITURE

        this.$snowplow.trackButtonClick(
          getInnerText(this.ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0].innerHtml),
          this.$config.v4Host + "/" + getV4UrlByCategoryId(categoryV4Id)
        )

        if (!categoryV4Id) {
          window.location.href = this.$config.v4Host + "/" + getV4UrlByCategoryId(categoryV4Id)
          this.submittingForm = false
          return
        }

        const payload = getV4ListingPayload(listing, categoryV4Id, this.$i18n.locale)

        if (payload.v === 2) {
          redirectToV2(payload, listing, this.$config.v4Host)
          this.submittingForm = false
          return
        }

        const listingService = new ListingService(this.$axios, this.$config)
        const response: ListingResponse = await listingService.postPreListing(
          payload,
          this.$i18n.locale
        )

        if (response?.data && response.data?.pre_listing_id && response.data?.category_id) {
          if (response.is_international) {
            redirectToV2(payload, listing, this.$config.v4Host)
            this.submittingForm = false
            return
          }

          addPreListingIdToSessionStorage(response.data)
          document.addEventListener("visibilitychange", () => {
            this.submittingForm = false
            this.submittedForm = false
            this.showFurnitureItems = false
          })
          window.location.href = getV4Url(
            this.$config.v4Host,
            response.data,
            payload,
            this.$i18n.locale
          )
          return
        } else {
          this.submittingForm = false
          if (this.getInventoryComputed) {
            this.$snowplow.trackCTAButtonFailed(
              "page",
              this.getItemsCount,
              "PreListing creation failed"
            )
          }
        }
      } catch (err) {
        this.submittingForm = false
        if (this.getInventoryComputed) {
          this.$snowplow.trackCTAButtonFailed(
            "page",
            this.getItemsCount,
            "PreListing creation error" + err
          )
        }
        console.log("PreListing creation error", err)
      }
    },

    hasValidation(block) {
      return patternCheck(block.attributes, VALIDATION)
    },

    onEmailFormOpen() {
      this.emailFormShow = true
    },

    onEmailFormClose() {
      this.emailFormShow = false
    },

    handleCallUsClick(label = "location_form_button"): void {
      this.$snowplow.trackCallUs({
        phoneNumber: this.getPhoneNumberValue.baseRaw,
        label,
        action: "click",
        extension: this.getPhoneNumberValue.extension
      })
    },

    hideItemsOnClickOutside(event): void {
      // If the user clicked at the Summary or Create/Edit Item dialog then we should not close the dropdown.
      if (event.target.closest(".extension-block") || event.target.closest("#modalContent")) return
      eventBus.$emit("close-dropdown")
      this.hideItems()
    },

    hideItems(): void {
      setTimeout(() => {
        this.showFurnitureItems = false
      }, 500)
    },

    // Wrapper for CTA Button Click
    onCTAClick() {
      // Trigger the existing submitForm logic
      this.submitForm()

      // Track the CTA button click event
      this.$snowplow.trackEvent({
        category: "get_instant_quote",
        action: "click"
      })
    }
  }
})
